import React, { useContext } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import '../../../css/DependentMultiChoice.scss';
import '../../../css/Multichoice.scss';
import { LessonMode } from '../../../Constants';

import questionService from '../../services/QuestionService';
import responseService from '../../services/ResponseService';
import utilsService from '../../services/UtilsService';

import { useViewportSize } from '../../../hooks/useViewportSize';

import FeedbackIcon from '../FeedbackIcon';
import Guideline from '../tools/Guideline';
import HtmlComponent from '../HtmlComponent';
import OptionalImage from '../OptionalImage';
import useStyleEvents from '../../../hooks/useStyleEvents';
import PrintQuestionNumber from './PrintQuestionNumber';

const optionNames = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const optionLetter = (index) => optionNames[index] ?? `${index + 1}`;

const DependentMultiChoice = observer(({ lessonElementId }) => {
  const {
    lessonManager,
    multichoiceManager,
    responseManager,
    toolbarManager,
    questionFeedbackManager
  } = useContext(MobXProviderContext);

  const { width: viewportWidth } = useViewportSize();

  const model = lessonManager.getSlideModel(lessonElementId);
  const lessonElementState = responseManager.getLessonElementState(lessonElementId);
  const feedbackState = questionFeedbackManager.getUiState(lessonElementId);

  const { isEliminatorActive } = toolbarManager;

  const readOnly = (lessonElementState && lessonElementState.readOnly) ||
    (feedbackState && feedbackState.showAnswers) ||
    lessonManager.isReadOnly;

  const { showAnswerFeedback } = feedbackState;
  const questions = model?.questions || [];
  const currentResponseAnswer = lessonElementState?.currentResponseAnswer;

  useStyleEvents(lessonElementId);

  const handleDataChange = (questionIndex, selectedId) => {
    responseService.responseChangeHandler({
      action: isEliminatorActive ? 'eliminate' : 'select',
      questionIndex,
      id: selectedId
    }, lessonElementId);

    if (isEliminatorActive) {
      toolbarManager.addContentToolUsage('Eliminator');
    }
  };

  const getCorrectAnswerIdForQuestion = (questionIndex) => {
    const answerSets = model.validation?.answerSets;
    if (!answerSets?.length) return '';
    const correctSet = answerSets[0];
    return correctSet[questionIndex] ?? '';
  };

  return (
    <div className='DependentMultiChoice'>
      {toolbarManager.isGuidelineOpen && <Guideline lessonElementId={lessonElementId} />}

      <div className='test-item-question' id={`test-item-question-${lessonElementId}`}>
        {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && <PrintQuestionNumber model={model} />}
      </div>
      <OptionalImage model={model} runtime={true} />

      <ul className='dependent-multi-choice-list' aria-label={model.slideSummary || 'Dependent Multi Choice'}>
        {questions.map((question, questionIndex) => {
          // Include questionIndex so each sub-question has its own order even if ids collide.
          const managerKey = `${lessonElementId}-dmc-${questionIndex}-${question.id || 'q'}`;
          const answers = question.answers || [];

          if (!multichoiceManager.hasMultichoiceItems(managerKey)) {
            const source = [...answers];
            const orderedAnswers = model?.isRandomized
              ? utilsService.guaranteedShuffle(source)
              : source;
            multichoiceManager.setMultichoiceItems(managerKey, orderedAnswers);
          } else {
            multichoiceManager.syncMultichoiceItemTexts(managerKey, answers);
          }

          const processedAnswers = lessonElementState
            ? multichoiceManager.getMultichoiceItems(managerKey) || []
            : [];

          const questionEntry = currentResponseAnswer?.answers?.[questionIndex];
          const selectedAnswerId = questionEntry?.id ?? '';
          const isAnswerEliminated = (id) =>
            id === selectedAnswerId && (questionEntry?.isEliminated ?? false);
          const correctAnswerId = getCorrectAnswerIdForQuestion(questionIndex);

          const handleClick = (data) => {
            const selectedId = data.target.value;
            handleDataChange(questionIndex, selectedId);
          };

          const handleKeyPress = (event) => {
            const getTargetValue = (target) => target.getElementsByTagName('input')[0]?.value;
            const radioId = getTargetValue(event.target);
            if (!radioId) return;
            if (event.code === 'Space') {
              handleDataChange(questionIndex, radioId);
            } else if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
              const currentlySelectedIndex = processedAnswers.findIndex((a) => a.id === radioId);
              let nextIndex = (currentlySelectedIndex + (event.code === 'ArrowUp' ? -1 : 1)) % processedAnswers.length;
              if (nextIndex < 0) nextIndex = processedAnswers.length - 1;
              const nextElement = event.target.parentElement.children[nextIndex];
              if (nextElement) {
                handleDataChange(questionIndex, getTargetValue(nextElement));
                nextElement.focus();
              }
            }
          };

          return (
            <li key={question.id} className='dependent-multi-choice-item'>
              <div className='multi-choice-question'>
                <fieldset>
                  <legend className='question-legend'>
                    <div className='test-item-question-sub'>
                      <HtmlComponent htmlStr={question.questionText} />
                    </div>
                  </legend>
                  <div
                    className='test-item-answers'
                    role='radiogroup'
                    aria-label={`Question ${questionIndex + 1}`}
                    style={questionService.getGenericMultichoiceStyleObjs({ model: { ...model, layout: undefined }, viewportWidth })?.answerContainerStyle}
                  >
                    {processedAnswers.map(({ id, text }, index) => {
                      const isSelected = id === selectedAnswerId;
                      const isCorrectAnswer = id === correctAnswerId;
                      const isEliminated = isAnswerEliminated(id);
                      const symbol = optionLetter(index);
                      let visualClass = '';
                      if (showAnswerFeedback && isSelected) {
                        visualClass = isCorrectAnswer ? 'correct' : 'incorrect';
                      }
                      const inputId = `${id}_${managerKey}`;
                      return (
                        <label
                          key={`${id}_${managerKey}`}
                          aria-checked={isSelected}
                          className={`radio-container ${isEliminated ? 'eliminated' : ''}`}
                          htmlFor={inputId}
                          onKeyDown={handleKeyPress}
                          role='radio'
                          style={questionService.getGenericMultichoiceStyleObjs({ model: { ...model, layout: undefined }, viewportWidth })?.answerDivStyle}
                          tabIndex={readOnly ? -1 : 0}
                        >
                          <div className='radio-text-container' tabIndex={-1}>
                            <input
                              checked={isSelected}
                              disabled={readOnly}
                              id={inputId}
                              onChange={handleClick}
                              tabIndex={-1}
                              type='radio'
                              value={id}
                            />
                            <label
                              className={`radio-symbol ${isSelected ? 'selected' : ''} ${visualClass} ${readOnly ? 'disabled' : ''}`}
                              htmlFor={inputId}
                              tabIndex={-1}
                            >
                              {symbol}
                            </label>
                            <label htmlFor={inputId} tabIndex={-1}>
                              <HtmlComponent htmlStr={text} />
                            </label>
                            {isSelected && (
                              <div className='correct-answer-symbol'>
                                <FeedbackIcon lessonElementId={lessonElementId} />
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
});

export default DependentMultiChoice;
